import { useCallback } from "react";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import * as Repository from "../../repository";
import { useHotelPolicyMutation } from "../../repository";

import type { AccommodationHotelPolicyFormModel } from "../../type";

type Props = {
	hotelID: string;
	hotelDisplaySettingsId: string;

	viewModel: AccommodationHotelPolicyFormModel["ViewModel"];
	onSuccessCallback: () => void;
};

export const useUpdateHotelPolicy = ({
	hotelID,
	hotelDisplaySettingsId,
	viewModel,
	onSuccessCallback,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { onUpdateHotelPolicy: onUpdate } = useHotelPolicyMutation(hotelID);

	const onUpdateHotelPolicy = useCallback(async () => {
		showLoadingScreenCallback();

		const hotelPolicyInput = Repository.Composition.viewModelToHotelPolicyInputConverter({
			viewModel,
		});
		const hotelDisplaySettingsInput = Repository.Composition.viewModelToHotelDisplaySettingsInputConverter({
			viewModel,
			hotelDisplaySettingsId,
			hotelID,
		});

		const result = await onUpdate({
			hotelPolicyInput,
			hotelDisplaySettingsInput,
		}).finally(hideLoadingScreenCallback);

		if (result.errors) {
			return;
		}

		const displaySettingsResult = result.data?.hotelDisplaySettingsUpdate;
		if (displaySettingsResult?.userErrors?.length) {
			notifyUserEventInfo({
				variant: "error",
				title: "ホテル表示設定を更新できませんでした",
				description: "時間を置いて再度お試しください",
			});
			return;
		}

		notifyUserEventInfo({
			title: "ホテルポリシーを更新しました",
		});
		onSuccessCallback();
	}, [
		showLoadingScreenCallback,
		viewModel,
		hotelDisplaySettingsId,
		hotelID,
		onUpdate,
		hideLoadingScreenCallback,
		onSuccessCallback,
	]);

	return {
		onUpdateHotelPolicy,
	};
};
